import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from 'react-query';
import { useInView } from 'react-intersection-observer';
import Masonry from 'react-masonry-css';
import api from '../config/api';
import SEO from '../components/SEO';
import { BRAND } from '../config/brand';
import ProfileCard from '../components/ProfileCard';
import { Search as SearchIcon, Filter, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * SearchPage
 *
 * Improvements applied:
 * - useInfiniteQuery for proper infinite scrolling and accumulation of pages
 * - controlled search input (keeps UI in sync with `q` param)
 * - ResizeObserver + requestAnimationFrame-based throttle for carousel arrow visibility
 * - accessibility attributes for filter buttons and carousel arrows
 * - clearer loading / fetching-next-page states
 */

const FILTERS = [
  { value: 'all', label: 'Все' },
  { value: 'profiles', label: 'Профили' },
  { value: 'tags', label: 'Теги' },
  { value: 'categories', label: 'Категории' }
];

const breakpointColumns = {
  default: 5,
  1400: 4,
  1100: 3,
  700: 2,
  500: 1,
};

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const type = searchParams.get('type') || 'all';
  const scope = (searchParams.get('scope') || '').toLowerCase();
  const isHelpScope = scope === 'help';

  // controlled input
  const [searchInput, setSearchInput] = useState(query);

  // carousel refs & state
  const filterCarouselRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // in-view ref for infinite scroll trigger
  const { ref: inViewRef, inView } = useInView();

  // --- react-query infinite query ---
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    status,
  } = useInfiniteQuery(
    ['search', query, type, scope],
    async ({ pageParam = 1 }) => {
      const response = await api.get(
        isHelpScope
          ? `/api/pages/help-search?q=${encodeURIComponent(query)}&page=${pageParam}&limit=24`
          : `/api/search?q=${encodeURIComponent(query)}&type=${encodeURIComponent(type)}&page=${pageParam}&limit=24`
      );
      return response.data;
    },
    {
      enabled: !!query,
      getNextPageParam: (lastPage) => {
        const { page: currentPage = 1, limit = 24, total = 0 } = lastPage.pagination || {};
        return currentPage * limit < total ? currentPage + 1 : undefined;
      },
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: (failureCount, error) => {
        if (error?.response?.status >= 400 && error?.response?.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    }
  );

  // flatten pages into results array
  const profiles = useMemo(
    () => (data ? data.pages.flatMap((p) => p.profiles || []) : []),
    [data]
  );

  const helpPages = useMemo(
    () => (data ? data.pages.flatMap((p) => p.pages || []) : []),
    [data]
  );

  const totalResults = data?.pages?.[0]?.pagination?.total || 0;

  // keep input in sync when `q` param changes externally
  useEffect(() => {
    setSearchInput(query);
  }, [query]);

  // trigger next page when sentinel is in view
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // handle submit
  const handleSearch = (e) => {
    e.preventDefault();
    const searchQuery = (searchInput || '').trim();
    if (searchQuery) {
      const next = { q: searchQuery };
      if (!isHelpScope) next.type = type;
      if (isHelpScope) next.scope = 'help';
      setSearchParams(next);
    }
  };

  const handleTypeChange = useCallback(
    (newType) => {
      if (isHelpScope) return;
      // keep current query but change type
      setSearchParams({ q: query || searchInput || '', type: newType });
      // scroll carousel start for clarity
      if (filterCarouselRef.current) filterCarouselRef.current.scrollLeft = 0;
    },
    [query, searchInput, setSearchParams, isHelpScope]
  );

  // carousel scroll helpers
  const handleCarouselScroll = useCallback((direction) => {
    const el = filterCarouselRef.current;
    if (!el) return;
    const scrollAmount = Math.min(200, el.clientWidth * 0.6);
    const target = direction === 'left' ? el.scrollLeft - scrollAmount : el.scrollLeft + scrollAmount;
    el.scrollTo({ left: target, behavior: 'smooth' });
  }, []);

  // visibility check: throttled using requestAnimationFrame
  const tickingRef = useRef(false);
  const checkArrowsVisibility = useCallback(() => {
    const el = filterCarouselRef.current;
    if (!el) {
      setShowLeftArrow(false);
      setShowRightArrow(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    tickingRef.current = false;
  }, []);

  const onScrollThrottled = useCallback(() => {
    if (tickingRef.current) return;
    tickingRef.current = true;
    requestAnimationFrame(() => checkArrowsVisibility());
  }, [checkArrowsVisibility]);

  // observe carousel size changes & scroll events
  useEffect(() => {
    const el = filterCarouselRef.current;
    if (!el) return;

    // initial check
    checkArrowsVisibility();

    // add scroll listener
    el.addEventListener('scroll', onScrollThrottled, { passive: true });

    // ResizeObserver for element size changes
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        // schedule visibility check
        onScrollThrottled();
      });
      ro.observe(el);
    } else {
      // fallback: window resize
      window.addEventListener('resize', onScrollThrottled);
    }

    return () => {
      el.removeEventListener('scroll', onScrollThrottled);
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', onScrollThrottled);
    };
  }, [onScrollThrottled, checkArrowsVisibility]);

  // reset scroll when filters/data change (small delay to let layout update)
  useEffect(() => {
    const el = filterCarouselRef.current;
    if (!el) return;
    const t = setTimeout(() => {
      checkArrowsVisibility();
    }, 120);
    return () => clearTimeout(t);
  }, [data, type, checkArrowsVisibility]);

  // render when no query provided
  if (!query) {
    return (
      <>
        <SEO
          title={isHelpScope ? 'Поиск по справке' : 'Поиск профилей'}
          description={isHelpScope
            ? `Найдите статьи справки на ${BRAND.name}.`
            : `Найдите профили, теги и категории на ${BRAND.name}.`}
          keywords={[
            'поиск', ...(isHelpScope ? ['справка', 'help'] : ['профили', 'теги', 'категории'])
          ]}
        />
        <div className="min-h-screen bg-secondary-50 py-8">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center py-16">
              <SearchIcon className="w-16 h-16 text-secondary-400 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-secondary-900 mb-4">
                Поиск удивительных идей
              </h1>
              <p className="text-secondary-600 mb-8 max-w-2xl mx-auto">
                {isHelpScope ? 'Найдите статьи справки' : 'Найдите профили, теги и категории'}
              </p>

              <form onSubmit={handleSearch} className="max-w-md mx-auto">
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-5 h-5" />
                  <input
                    name="search"
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Поиск идей..."
                    className="w-full pl-10 pr-4 py-3 border border-secondary-200 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={`Поиск: ${query} - Результаты поиска`}
        description={`Найдено ${totalResults} результатов по запросу "${query}".`}
        keywords={[query, 'поиск', 'результаты', 'профили', 'теги', 'категории']}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SearchResultsPage",
          "name": `Поиск: ${query}`,
          "description": `Результаты поиска по запросу "${query}"`,
          "url": `${typeof window !== 'undefined' ? window.location.href : `${BRAND.baseUrl}/search?q=${encodeURIComponent(query)}`}`,
          "inLanguage": "ru-RU",
          "numberOfItems": totalResults,
          "query": query
        }}
      />
      <div className="min-h-screen bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Search Header */}
          <div className="mb-8">
            <form onSubmit={handleSearch} className="mb-6">
              <div className="relative max-w-2xl">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-5 h-5" />
                <input
                  name="search"
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Поиск идей..."
                  className="w-full pl-10 pr-4 py-3 border border-secondary-200 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </form>

            {/* Search Filters */}
            {!isHelpScope && (
              <div className="flex items-center space-x-4 mb-6">
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <Filter className="w-4 h-4 text-secondary-600" />
                  <span className="text-sm font-medium text-secondary-700">Фильтр по:</span>
                </div>

                {/* Filter Carousel */}
                <div className="relative flex-1 min-w-0">
                  <div className="relative">
                    {/* Left Arrow */}
                    {showLeftArrow && (
                      <button
                        onClick={() => handleCarouselScroll('left')}
                        aria-label="Прокрутить влево"
                        className="absolute left-0 top-0 bottom-0 z-10 flex items-center justify-start pl-2 text-secondary-600 hover:text-secondary-900 transition-colors"
                        style={{
                          background: 'linear-gradient(90deg, #fff 25%, #fff0)',
                          width: '60px'
                        }}
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                    )}

                    {/* Right Arrow */}
                    {showRightArrow && (
                      <button
                        onClick={() => handleCarouselScroll('right')}
                        aria-label="Прокрутить вправо"
                        className="absolute right-0 top-0 bottom-0 z-10 flex items-center justify-end pr-2 text-secondary-600 hover:text-secondary-900 transition-colors"
                        style={{
                          background: 'linear-gradient(270deg, #fff 25%, #fff0)',
                          width: '60px'
                        }}
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    )}

                    {/* Filter Buttons */}
                    <div
                      ref={filterCarouselRef}
                      className="flex space-x-2 overflow-x-auto scrollbar-hide px-8"
                      style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                      {FILTERS.map((filter) => (
                        <button
                          key={filter.value}
                          onClick={() => handleTypeChange(filter.value)}
                          aria-pressed={type === filter.value}
                          aria-label={`Фильтр: ${filter.label}`}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 min-w-max ${type === filter.value
                            ? 'bg-primary-500 text-white'
                            : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                            }`}
                        >
                          {filter.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Results Info */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-secondary-900">
                  Результаты поиска для "{query}"
                </h1>
                <p className="text-secondary-600">
                  Найдено {totalResults} {totalResults === 1 ? 'результат' : totalResults < 5 ? 'результата' : 'результатов'}
                </p>
              </div>
              <div className="text-sm text-secondary-500">
                Показано {(isHelpScope ? helpPages.length : profiles.length)} из {totalResults}
              </div>
            </div>
          </div>

          {/* Search Results */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
                <p className="text-secondary-600">Поиск...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">😔</div>
              <h2 className="text-2xl font-semibold text-secondary-900 mb-2">
                Поиск не удался
              </h2>
              <p className="text-secondary-600 mb-4">
                Не удалось выполнить поиск. Попробуйте еще раз.
              </p>
              <button
                onClick={() => refetch()}
                className="btn btn-primary"
              >
                Попробовать снова
              </button>
            </div>
          ) : isHelpScope ? (
            helpPages.length > 0 ? (
              <div className="space-y-3">
                {helpPages.map((p) => (
                  <div key={p.slug} className="bg-white border border-secondary-200 rounded-lg p-4 shadow-sm">
                    <Link className="text-base font-semibold link" to={p.url || ('/' + p.slug)}>
                      {p.title || p.slug}
                    </Link>
                    {p.description && (
                      <p className="text-sm text-secondary-700 mt-1">{p.description}</p>
                    )}
                  </div>
                ))}

                {/* Infinite Scroll Trigger */}
                <div ref={inViewRef} className="flex items-center justify-center py-8">
                  {isFetchingNextPage ? (
                    <div className="flex items-center space-x-2">
                      <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
                      <span className="text-secondary-600">Загрузка дополнительных результатов...</span>
                    </div>
                  ) : hasNextPage ? (
                    <div className="text-secondary-500">Прокрутите для загрузки еще</div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-secondary-500">Вы достигли конца результатов!</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🔎</div>
                <h2 className="text-2xl font-semibold text-secondary-900 mb-2">
                  Ничего не найдено в справке
                </h2>
                <p className="text-secondary-600 mb-4">
                  Попробуйте другой запрос.
                </p>
                <div className="text-sm text-secondary-500">
                  <Link className="link" to="/help">Перейти в справку</Link>
                </div>
              </div>
            )
          ) : profiles.length > 0 ? (
            <>
              <Masonry
                breakpointCols={breakpointColumns}
                className="masonry-grid"
                columnClassName="masonry-grid_column"
              >
                {profiles.map((p) => (
                  <div key={p.id} className="mb-4">
                    <ProfileCard profile={p} />
                  </div>
                ))}
              </Masonry>

              {/* Infinite Scroll Trigger */}
              <div ref={inViewRef} className="flex items-center justify-center py-8">
                {isFetchingNextPage ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
                    <span className="text-secondary-600">Загрузка дополнительных результатов...</span>
                  </div>
                ) : hasNextPage ? (
                  <div className="text-secondary-500">Прокрутите для загрузки еще</div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-secondary-500">Вы достигли конца результатов! 🎉</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔍</div>
              <h2 className="text-2xl font-semibold text-secondary-900 mb-2">
                Результаты не найдены
              </h2>
              <p className="text-secondary-600 mb-4">
                Попробуйте изменить поисковые запросы или фильтры
              </p>
              <div className="space-y-2 text-sm text-secondary-500">
                <p>• Проверьте правильность написания</p>
                <p>• Попробуйте другие ключевые слова</p>
                <p>• Используйте более общие поисковые термины</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchPage;
